import { Fragment } from 'react';
import type { SnippetLang } from '../lib/requestSnippets';

/**
 * A tiny, dependency-free syntax highlighter for the three request-snippet
 * languages (cURL/bash, Node, Python) plus JSON responses and the generated
 * TypeScript response interface. It tokenizes with a
 * small ordered set of sticky regexes into semantic roles, then colors each role
 * from one of two palettes: the fixed dark `--term-syn-*` terminal palette, or the
 * theme-aware brand palette for light design-system cards. Scoped to these
 * controlled snippets - not a general-purpose lexer.
 */

/** The languages CodeHighlight can color: the snippet langs, a JSON response,
 *  and a TypeScript interface. */
export type HighlightLang = SnippetLang | 'json' | 'ts';

/** Which surface the code sits on: the dark terminal, or a light design card. */
type HighlightVariant = 'terminal' | 'card';

/** A semantic token role, mapped to a color per palette (see TERM / CARD). */
type Role = 'str' | 'key' | 'num' | 'fn' | 'comment' | 'url';

interface Rule {
  re: RegExp;
  role: Role;
}

// `y` (sticky) so each rule only matches at the current scan position.
const dq: Rule = { re: /(?:[fr]{1,2})?"(?:\\.|[^"\\])*"/y, role: 'str' };
const sq: Rule = { re: /(?:[fr]{1,2})?'(?:\\.|[^'\\])*'/y, role: 'str' };
const tmpl: Rule = { re: /`(?:\\.|[^`\\])*`/y, role: 'str' };
const url: Rule = { re: /https?:\/\/[^\s"'`]+/y, role: 'url' };
const num: Rule = { re: /\d+(?:\.\d+)?/y, role: 'num' };
const fn: Rule = { re: /[A-Za-z_$][\w$]*(?=\s*\()/y, role: 'fn' };

const kw = (words: string[]): Rule => ({ re: new RegExp(`\\b(?:${words.join('|')})\\b`, 'y'), role: 'key' });

// JSON: an object key ("foo":) reads as the fn color so keys stand apart from string
// values; the value string keeps the str color. The key rule must precede the generic
// string rule, and booleans/null (key role) precede numbers.
const jsonKey: Rule = { re: /"(?:\\.|[^"\\])*"(?=\s*:)/y, role: 'fn' };
const jsonStr: Rule = { re: /"(?:\\.|[^"\\])*"/y, role: 'str' };
const jsonLit: Rule = { re: /\b(?:true|false|null)\b/y, role: 'key' };
const jsonNum: Rule = { re: /-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?/y, role: 'num' };

const RULES: Record<HighlightLang, Rule[]> = {
  curl: [
    { re: /#[^\n]*/y, role: 'comment' },
    dq,
    sq,
    url,
    { re: /--?[A-Za-z][\w-]*/y, role: 'key' }, // flags: -X, -H, -d, --transport
    { re: /curl\b/y, role: 'fn' },
    num,
  ],
  node: [
    { re: /\/\/[^\n]*/y, role: 'comment' },
    dq,
    sq,
    tmpl,
    url,
    kw(['const', 'let', 'var', 'await', 'async', 'return', 'new', 'function', 'import', 'from', 'true', 'false', 'null', 'undefined']),
    fn,
    num,
  ],
  python: [
    { re: /#[^\n]*/y, role: 'comment' },
    dq,
    sq,
    url,
    kw(['import', 'from', 'as', 'def', 'return', 'None', 'True', 'False', 'with', 'for', 'in', 'if', 'else', 'os']),
    fn,
    num,
  ],
  json: [jsonKey, jsonStr, jsonLit, jsonNum],
  // A property name (`likes`, `avatarUrl?`) reads in ink like a JSON key, and
  // its type in the keyword colour, so the eye runs down names then types.
  ts: [
    { re: /\/\/[^\n]*/y, role: 'comment' },
    dq,
    kw(['interface', 'type', 'export', 'string', 'number', 'boolean', 'null', 'undefined', 'unknown', 'true', 'false']),
    { re: /[A-Za-z_$][\w$]*(?=\??:)/y, role: 'fn' },
    num,
  ],
};

// Fixed dark palette for code on a dark panel (the landing DeveloperPanel -
// reads the same in light and dark mode).
const TERM: Record<Role, string> = {
  str: 'text-[var(--term-syn-str)]',
  key: 'text-[var(--term-syn-key)]',
  num: 'text-[var(--term-syn-num)]',
  fn: 'text-[var(--term-syn-fn)]',
  comment: 'text-[var(--term-syn-comment)]',
  url: 'text-[var(--term-syn-url)]',
};

// Restrained, standard-editor palette for code on a light design-system card: a
// GitHub-light-style scheme in three hues (green strings, blue keywords/numbers,
// muted comments) with function names and JSON keys in plain ink. Tokens shift
// with the theme so the same colors read on both light and dark surfaces.
const CARD: Record<Role, string> = {
  str: 'text-[var(--pos)]',
  key: 'text-[var(--accent-text)]',
  num: 'text-[var(--accent-text)]',
  fn: 'text-[var(--text)]',
  comment: 'text-[var(--text-3)]',
  url: 'text-[var(--pos)]',
};

export interface Tok {
  role?: Role;
  text: string;
}

/** Splits `code` into roled/plain tokens for `lang`. Exported for unit tests. */
export const tokenize = (code: string, lang: HighlightLang): Tok[] => {
  const rules = RULES[lang];
  const out: Tok[] = [];
  let plain = '';
  const flush = () => {
    if (plain) {
      out.push({ text: plain });
      plain = '';
    }
  };
  let i = 0;
  while (i < code.length) {
    let matched = false;
    for (const { re, role } of rules) {
      re.lastIndex = i;
      const m = re.exec(code);
      if (m && m[0].length > 0) {
        flush();
        out.push({ role, text: m[0] });
        i += m[0].length;
        matched = true;
        break;
      }
    }
    if (!matched) {
      plain += code[i];
      i += 1;
    }
  }
  flush();
  return out;
};

export interface CodeHighlightProps {
  code: string;
  lang: HighlightLang;
  /** Color palette: the dark terminal (default) or a light design-system card. */
  variant?: HighlightVariant;
}

/** Renders `code` as colored tokens for the chosen surface. */
export const CodeHighlight = ({ code, lang, variant = 'terminal' }: CodeHighlightProps) => {
  const palette = variant === 'card' ? CARD : TERM;
  return (
    <>
      {tokenize(code, lang).map((t, i) =>
        t.role ? (
          <span key={i} className={palette[t.role]}>
            {t.text}
          </span>
        ) : (
          <Fragment key={i}>{t.text}</Fragment>
        ),
      )}
    </>
  );
};
